import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { v4 as uuid } from 'uuid';
import { message } from '$/components/Message';
import { InlineBoxType, type IInlineBoxData } from '$/components/InlineBox/types';
import { serializeContentToInlineText } from '$/components/InputTextAreaV2/serialize';
import type { EditorSerializedContent, MentionValue, SetValueOptions } from '$/components/InputTextAreaV2/types';
import { useLanguageStore } from '$/store/languageStore';
import { getSoraStatus } from '$/api/canvasRquest';
import { roomItemModelList } from '$/api/server';
import type {
  AgentModelCategory,
  UsedModel,
} from '$/domains/agentModel/type';
import { modelMapFormat, modelMapFormatMenuDetail } from '$/domains/agentModel/utlils';
import { getHomeInputPrompt } from '$/domains/caseV3/caseV3Service';
import type { InputPromptSkillItem, V3CanvasHomeCaseItem } from '$/domains/caseV3/type';
import {
  dealUploadFile,
  FileTypeEnum,
  formatFileType,
  getVideoCoverUrl,
  handleClickUploadFile,
  type CallbackFiles,
} from '$/utils/upload';
import type { InputPanelAssetModalListItem } from '$/components/InputPanelToolBar/types';
import { parsePopupDataToEditorContent } from '$/utils/messageUtil';
import { mtstatTrack } from '$/utils/mtstat';
import { INPUT_MEDIA_UPLOAD_COUNT_LIMITS } from '$/utils/mediaUploadLimits';
import type {
  InputComposerActions,
  InputComposerOptions,
  InputComposerRemoveResourceOptions,
  InputComposerResource,
  InputComposerSession,
  UseInputComposerResult,
} from './types';

export type {
  InputComposerActions,
  InputComposerConfig,
  InputComposerOptions,
  InputComposerRemoveResourceOptions,
  InputComposerResource,
  InputComposerSession,
  InputComposerSurface,
  InputComposerTrackLocation,
  UseInputComposerResult,
} from './types';

enum InputComposerCancelStatus {
  Default = 'default',
  Cancel = 'cancel',
  Cancelling = 'cancelling',
}

const DEFAULT_TRACK_LOCATION = 'home_page';
export const DEFAULT_INPUT_COMPOSER_UPLOAD_LIMITS = {
  ...INPUT_MEDIA_UPLOAD_COUNT_LIMITS,
};
const INLINE_TOKEN_REGEXP = /\[@([a-zA-Z_]+)(?::#([^:]*))?:([^\]]*)\]/g;

export const DEFAULT_HOME_INPUT_COMPOSER_ID = 'home:main';

/** 创建空模型分类，保证输入框初始状态具备完整字段。 */
function createDefaultModelCategory(): AgentModelCategory {
  return {
    modelCategoryTitle: '',
    modelItems: [],
  };
}

/** 创建默认已选模型，和 Chat/TeamAgent 现有 store 的初始结构保持一致。 */
function createDefaultUsedModel(): UsedModel {
  return {
    activeType: 'imageModel',
    imageModel: {
      modelDesc: '',
      modelName: '',
      modelValue: '',
      enable: false,
    },
    videoModel: {
      modelDesc: '',
      modelName: '',
      modelValue: '',
      enable: false,
    },
  };
}

/** 创建默认工具显隐配置，记录接口默认值供后续重置。 */
function createDefaultInputToolVisibility() {
  return {
    showDocumentUpload: false,
    showModelSelect: false,
    showSelectMode: false,
    showAgentThinkingMode: false,
  };
}

/** 创建单个输入框 hook 实例的默认状态。 */
function createDefaultInputComposerSession(): InputComposerSession {
  return {
    inputText: '',
    resourceList: [],
    uploading: false,
    uploadingCount: 0,
    uploadPopoverOpen: false,
    assetLibraryVisible: false,
    showDocumentUpload: false,
    showModelSelect: false,
    showSelectMode: false,
    defaultInputToolsVisibleValues: createDefaultInputToolVisibility(),
    modelMap: {
      imageModel: createDefaultModelCategory(),
      videoModel: createDefaultModelCategory(),
    },
    usedModel: createDefaultUsedModel(),
    activeMode: 'normal',
    showAgentThinkingMode: false,
    agentThinkingModeType: '',
    agentThinkingModeList: [],
    modelPatternInfoList: [],
    showSkillSelect: true,
    skillCategories: [],
    skillList: [],
    selectedSkill: [],
    agentInfoList: [],
  };
}

/** 根据序列化标签和资源类型还原输入框 inline 节点类型。 */
function resolveInlineBoxType(tag: string, resource: InputComposerResource): InlineBoxType {
  if (tag === 'input_image' || resource.mediaType === 'image') return InlineBoxType.Image;
  if (tag === 'input_video' || resource.mediaType === 'video') return InlineBoxType.Video;
  if (tag === 'input_file' || resource.mediaType === 'file') return InlineBoxType.File;
  if (tag === 'input_skill' || resource.mediaType === 'skill') return InlineBoxType.Skill;
  return InlineBoxType.Mark;
}

/** 把 hook 内保存的格式化文本和资源列表还原成输入框需要的结构化内容。 */
function buildEditorContentFromInputText(
  inputText: string,
  resourceList: InputComposerResource[],
  agentInfoList: MentionValue[] = [],
): EditorSerializedContent {
  const content: EditorSerializedContent = [];
  const resourceMap = new Map(resourceList.map((resource) => [resource.index, resource]));
  const agentMap = new Map(agentInfoList.map((agent) => [`${agent.id}`, agent]));
  let lastIndex = 0;

  inputText.replace(INLINE_TOKEN_REGEXP, (token, tag: string, order: string | undefined, index: string, offset: number) => {
    if (offset > lastIndex) {
      content.push({ type: 'text', value: inputText.slice(lastIndex, offset) });
    }

    if (tag === 'agent') {
      const agent = agentMap.get(index);
      content.push({ type: 'mention', value: { id: index, name: agent?.name || index } });
    } else {
      const resource = resourceMap.get(index);
      if (resource) {
        content.push({
          type: 'inline-box',
          value: {
            type: resolveInlineBoxType(tag, resource),
            data: {
              ...resource,
              order: order ? Number(order) : resource.order,
            },
          },
        });
      } else {
        content.push({ type: 'text', value: token });
      }
    }

    lastIndex = offset + token.length;
    return token;
  });

  if (lastIndex < inputText.length) {
    content.push({ type: 'text', value: inputText.slice(lastIndex) });
  }

  return content;
}

/** 从结构化输入内容提取 Agent mention，保留显示名供后续回显。 */
function getAgentInfoListFromEditorContent(content: EditorSerializedContent): MentionValue[] {
  const agentMap = new Map<string, MentionValue>();
  content.forEach((fragment) => {
    if (fragment.type !== 'mention') return;
    const id = `${fragment.value.id}`;
    if (!id || agentMap.has(id)) return;
    agentMap.set(id, { id, name: fragment.value.name || id });
  });
  return Array.from(agentMap.values());
}

/** 从 popupData 中归一化 Agent 信息，兼容后端和分享入口的松散字段。 */
function normalizePopupAgentInfoList(agentInfoList?: Record<string, any>[]): MentionValue[] {
  if (!agentInfoList?.length) return [];
  const agentMap = new Map<string, MentionValue>();
  agentInfoList.forEach((item) => {
    const id = `${item.id ?? item.agent_id ?? ''}`;
    if (!id || agentMap.has(id)) return;
    agentMap.set(id, {
      id,
      name: item.name || item.agent_name || id,
    });
  });
  return Array.from(agentMap.values());
}

/** 保留格式化文本中仍存在的 Agent 显示名，避免外部纯文本回填后残留旧 Agent。 */
function filterAgentInfoListByInputText(inputText: string, agentInfoList: MentionValue[]) {
  const agentIds = new Set<string>();
  inputText.replace(INLINE_TOKEN_REGEXP, (_token, tag: string, _order: string | undefined, index: string) => {
    if (tag === 'agent' && index) {
      agentIds.add(index);
    }
    return _token;
  });
  if (!agentIds.size) return [];
  return agentInfoList.filter((agent) => agentIds.has(`${agent.id}`));
}

/** 根据当前已选 Skill 和完整列表获取最新 Skill 详情。 */
function getSelectedSkillInfoList(
  selectedSkill: InputPromptSkillItem[],
  skillList: UseInputComposerResult['skillList'],
) {
  if (!selectedSkill.length) return [];
  return selectedSkill
    .map((selected) => skillList.find(
      (skill) => skill.id === selected.id && skill.category_id === selected.category_id,
    ) || selected)
    .filter(Boolean) as UseInputComposerResult['skillList'];
}

/** 将单个或多个完整 Skill 入参归一化为数组，保留提交所需的完整字段。 */
function normalizeSelectedSkill(skill: InputPromptSkillItem | InputPromptSkillItem[] | null) {
  if (!skill) return [];
  const skillList = Array.isArray(skill) ? skill : [skill];
  return skillList.filter((item) => item.id && item.category_id);
}

/** 判断资产库素材是否应作为文档类资源处理。 */
function isDocumentAssetType(materialType: string) {
  return ['audio', 'document', 'excel', 'ppt', 'file', 'text', 'script', 'markdown'].includes(materialType);
}

/** 读取历史资产库 asset_id，新素材库条目不混用该字段。 */
function resolveInputComposerAssetId(item: InputPanelAssetModalListItem) {
  const raw = (item.raw ?? {}) as Record<string, any>;
  if (resolveInputComposerMediaLibraryItemId(item) != null) {
    return undefined;
  }
  const value = raw.assetId ?? raw.asset_id ?? item.id ?? raw.id;
  const id = Number(value);
  return Number.isFinite(id) ? id : undefined;
}

/** 读取新版素材库 item_id，供后续聊天发送链路区分素材库来源。 */
function resolveInputComposerMediaLibraryItemId(item: InputPanelAssetModalListItem) {
  const raw = (item.raw ?? {}) as Record<string, any>;
  const value = raw.mediaLibraryItemId ?? raw.media_library_item_id;
  const id = Number(value);
  return Number.isFinite(id) ? id : undefined;
}

/** 判断两个输入资源是否为同一个输入框资源，仅使用输入框内稳定的 index。 */
function isSameInputComposerResource(candidate: Partial<InputComposerResource>, target: InputComposerResource) {
  if (candidate.index && target.index) {
    return candidate.index === target.index;
  }
  return false;
}

/** 从结构化编辑器内容中移除对指定资源的 inline 引用。 */
function removeResourceRefsFromEditorContent(
  content: EditorSerializedContent,
  resource: InputComposerResource,
) {
  return content.filter((fragment) => {
    if (fragment.type !== 'inline-box') return true;
    return !isSameInputComposerResource(fragment.value.data, resource);
  });
}

/** 提取结构化编辑器内容中引用过的资源 index，用于删除资源时判断是否需要清理 undo 栈。 */
function getReferencedResourceIndexSetFromEditorContent(content: EditorSerializedContent) {
  const indexSet = new Set<string>();
  content.forEach((fragment) => {
    if (fragment.type !== 'inline-box') return;
    const { index } = fragment.value.data;
    if (!index) return;
    indexSet.add(`${index}`);
  });
  return indexSet;
}

/** 将上传回调文件转换为输入框资源。 */
function mapUploadedFilesToResources(files: any[], mediaType: 'image' | 'video' | 'file') {
  return files.map((file) => ({
    ...file,
    source: 'input',
    index: uuid(),
    mediaType,
    name: file.name || '',
    coverUrl: mediaType === 'video' ? file.coverUrl || '' : file.coverUrl,
  })) as IInlineBoxData[];
}

/** 将拖拽/粘贴上传回调文件转换为输入框资源，音频归入文件类预览。 */
function mapUploadCallbackFilesToResources(files: CallbackFiles[]) {
  return files.map((file) => {
    let mediaType: 'image' | 'video' | 'file' = 'file';
    if (file.type === FileTypeEnum.Image) {
      mediaType = 'image';
    }
    if (file.type === FileTypeEnum.Video) {
      mediaType = 'video';
    }

    return {
      ...file,
      source: 'input',
      index: uuid(),
      mediaType,
      name: file.name || '',
      coverUrl: mediaType === 'video' ? file.coverUrl || '' : file.coverUrl,
    };
  }) as IInlineBoxData[];
}

/** 将资产库素材转换为输入框资源。 */
function mapAssetItemsToResources(items: InputPanelAssetModalListItem[]) {
  const resources: IInlineBoxData[] = [];
  items.forEach((item) => {
    const displayName = item.raw?.name || item.id;
    const assetId = resolveInputComposerAssetId(item);
    const mediaLibraryItemId = resolveInputComposerMediaLibraryItemId(item);
    const sourceIds = {
      ...(assetId != null ? { asset_id: assetId } : {}),
      ...(mediaLibraryItemId != null ? {
        mediaLibraryItemId,
        media_library_item_id: mediaLibraryItemId,
      } : {}),
    };
    if (item.materialType === 'image') {
      resources.push({
        source: 'asset',
        index: uuid(),
        url: item.mediaUrl,
        name: displayName,
        mediaType: 'image',
        width: item.raw?.width,
        height: item.raw?.height,
        ...sourceIds,
      });
      return;
    }
    if (item.materialType === 'video') {
      resources.push({
        source: 'asset',
        index: uuid(),
        url: item.mediaUrl,
        coverUrl: item.coverUrl || getVideoCoverUrl(item.mediaUrl),
        compressUrl: item.raw?.compress_url,
        watermarkCompressUrl: item.raw?.watermark_compress_url,
        name: displayName,
        mediaType: 'video',
        ...sourceIds,
      });
      return;
    }
    if (isDocumentAssetType(item.materialType)) {
      resources.push({
        source: 'asset',
        index: uuid(),
        url: item.mediaUrl,
        coverUrl: '',
        name: displayName,
        mediaType: 'file',
        ...sourceIds,
      });
    }
  });
  return resources;
}

/** 从 V3 首页案例构建资源区和格式化输入文本。 */
export function buildInputComposerContentFromCase(item: V3CanvasHomeCaseItem) {
  const resourceList: InputComposerResource[] = [];
  const allMedia = [
    ...(item.cover_media ? [item.cover_media] : []),
    ...(item.input_media || []),
  ];

  allMedia.forEach((media) => {
    if (media.type === 'image') {
      resourceList.push({
        url: media.url,
        name: media.type,
        index: uuid(),
        mediaType: 'image',
        coverUrl: media.cover,
        source: 'callword',
        width: media.width,
        height: media.height,
        asset_id: media.asset_id,
      });
      return;
    }
    if (media.type === 'video') {
      resourceList.push({
        url: media.url,
        name: media.type,
        index: uuid(),
        mediaType: 'video',
        coverUrl: media.cover || getVideoCoverUrl(media.url),
        source: 'callword',
        width: media.width,
        height: media.height,
        asset_id: media.asset_id,
      });
    }
  });

  if (item.prompt || item.desc) {
    return { inputText: item.prompt || item.desc, resourceList };
  }

  return { inputText: '', resourceList };
}

/** 使用统一输入框状态、资源分离和底部工具交互。 */
export default function useInputComposer({
  config = {},
  beforeOpenAssetLibrary,
  onAssetLibraryOpen,
}: InputComposerOptions): UseInputComposerResult {
  const [session, setSession] = useState<InputComposerSession>(() => createDefaultInputComposerSession());
  const languageStore = useLanguageStore((state) => state.languageStore);
  const inputRef = useRef<InputComposerActions['inputRef']['current']>(null);
  const shouldSyncInputRef = useRef(false);
  const shouldResetInputHistoryRef = useRef(false);
  const syncInputSetValueOptionsRef = useRef<SetValueOptions | undefined>(undefined);
  const referencedResourceIndexSetRef = useRef<Set<string>>(new Set());
  const isCancelUpload = useRef(InputComposerCancelStatus.Default);

  const maxImageNum = config.maxImageNum ?? DEFAULT_INPUT_COMPOSER_UPLOAD_LIMITS.maxImageNum;
  const maxVideoNum = config.maxVideoNum ?? DEFAULT_INPUT_COMPOSER_UPLOAD_LIMITS.maxVideoNum;
  const maxDocumentNum = config.maxDocumentNum ?? DEFAULT_INPUT_COMPOSER_UPLOAD_LIMITS.maxDocumentNum;
  const maxResourceNum = config.maxResourceNum ?? Number.POSITIVE_INFINITY;
  const trackLocation = config.trackLocation ?? DEFAULT_TRACK_LOCATION;

  /** 更新当前 hook 实例的输入框状态，保证同页面多个实例天然隔离。 */
  const setSessionState = useCallback((
    updater: Partial<InputComposerSession> | ((current: InputComposerSession) => Partial<InputComposerSession>),
  ) => {
    setSession((current) => {
      const patch = typeof updater === 'function' ? updater(current) : updater;
      return {
        ...current,
        ...patch,
      };
    });
  }, []);

  const editorContent = useMemo(
    () => buildEditorContentFromInputText(session.inputText, session.resourceList, session.agentInfoList),
    [session.agentInfoList, session.inputText, session.resourceList],
  );
  const serializedContent = session.inputText;

  const images = useMemo(
    () => session.resourceList.filter((resource) => resource.mediaType === 'image'),
    [session.resourceList],
  );
  const videos = useMemo(
    () => session.resourceList.filter((resource) => resource.mediaType === 'video'),
    [session.resourceList],
  );
  const documents = useMemo(
    () => session.resourceList.filter((resource) => resource.mediaType === 'file'),
    [session.resourceList],
  );
  const atImageOptions = useMemo(
    () => session.resourceList.map((item) => ({
      id: item.index,
      name: item.name || '',
      keywords: [item.name, item.mediaType].filter(Boolean) as string[],
      image: item,
    })),
    [session.resourceList],
  );

  const selectedSkillInfoList = useMemo(
    () => getSelectedSkillInfoList(session.selectedSkill, session.skillList),
    [session.selectedSkill, session.skillList],
  );
  const selectedSkillInfo = selectedSkillInfoList[0] || null;

  const activeUsedModelType = session.usedModel.activeType || 'imageModel';
  const activeUsedModel = session.usedModel[activeUsedModelType as keyof typeof session.usedModel] as any;
  const usedModelName = activeUsedModel?.modelName || '';

  const isSubmitDisabled = (!serializedContent?.trim() && !session.resourceList.length) || session.uploading;
  const resourceTotal = session.resourceList.length;
  const remainingResourceNum = Math.max(0, maxResourceNum - resourceTotal);

  const imageUploadDisabled = useMemo(
    () => session.uploading || images.length >= maxImageNum
      || remainingResourceNum <= 0,
    [images.length, maxImageNum, remainingResourceNum, session.uploading],
  );

  const videoUploadDisabled = useMemo(
    () => session.uploading || videos.length >= maxVideoNum
      || remainingResourceNum <= 0,
    [maxVideoNum, remainingResourceNum, session.uploading, videos.length],
  );

  const documentUploadDisabled = useMemo(
    () => session.uploading || documents.length >= maxDocumentNum
      || remainingResourceNum <= 0,
    [documents.length, maxDocumentNum, remainingResourceNum, session.uploading],
  );

  /** 更新格式化输入文本，本地输入变化跳过 ref 回写，外部回填需要同步到输入框。 */
  const setInputText = useCallback((text: string, options?: { syncInput?: boolean; }) => {
    shouldSyncInputRef.current = !!options?.syncInput;
    setSessionState((current) => ({
      inputText: text,
      agentInfoList: filterAgentInfoListByInputText(text, current.agentInfoList),
    }));
  }, [setSessionState]);

  /** 将输入框结构化变更序列化为格式化文本后保存。 */
  const setEditorContent = useCallback((content: EditorSerializedContent) => {
    shouldSyncInputRef.current = false;
    getReferencedResourceIndexSetFromEditorContent(content).forEach((index) => {
      referencedResourceIndexSetRef.current.add(index);
    });
    setSessionState({
      inputText: serializeContentToInlineText(content),
      agentInfoList: getAgentInfoListFromEditorContent(content),
    });
  }, [setSessionState]);

  /** 将分享/弹窗写入的 popupData 还原到输入框状态，并补齐资源、Skill 和 Agent 显示名。 */
  const setPopupDataContent = useCallback((data: Record<string, any>) => {
    const content = parsePopupDataToEditorContent(data) as EditorSerializedContent;
    shouldSyncInputRef.current = true;
    referencedResourceIndexSetRef.current = getReferencedResourceIndexSetFromEditorContent(content);
    setSessionState({
      inputText: serializeContentToInlineText(content),
      resourceList: data.markElementList || [],
      selectedSkill: normalizeSelectedSkill(data.skillList || null),
      agentInfoList: normalizePopupAgentInfoList(data.agentInfoList),
    });
    return content;
  }, [setSessionState]);

  /** 记录当前派生内容中出现过的资源引用，保留手动删除前的引用痕迹供资源删除时清 undo 栈。 */
  useEffect(() => {
    getReferencedResourceIndexSetFromEditorContent(editorContent).forEach((index) => {
      referencedResourceIndexSetRef.current.add(index);
    });
  }, [editorContent]);

  /** 外部状态要求回填输入框时，按本次同步场景恢复选区和历史栈。 */
  useEffect(() => {
    if (!shouldSyncInputRef.current && !shouldResetInputHistoryRef.current) {
      return;
    }
    const shouldSyncInput = shouldSyncInputRef.current;
    const shouldResetInputHistory = shouldResetInputHistoryRef.current;
    const setValueOptions = syncInputSetValueOptionsRef.current;
    shouldSyncInputRef.current = false;
    shouldResetInputHistoryRef.current = false;
    syncInputSetValueOptionsRef.current = undefined;
    if (shouldSyncInput) {
      inputRef.current?.setValue(editorContent, setValueOptions);
    }
    if (shouldResetInputHistory) {
      inputRef.current?.resetHistoryStack();
    }
  }, [editorContent]);

  const addResources = useCallback((resources: InputComposerResource[]) => {
    if (!resources.length) return;
    setSessionState((current) => ({
      resourceList: [...current.resourceList, ...resources],
    }));
  }, [setSessionState]);

  /** 替换资源区列表，用于业务侧案例回填等完整覆盖场景。 */
  const setResources = useCallback((resources: InputComposerResource[]) => {
    setSessionState({ resourceList: resources });
  }, [setSessionState]);

  /** 删除资源列表项，并按调用方配置同步删除输入框中的资源引用和历史栈。 */
  const removeResource = useCallback((resource: InputComposerResource, options?: InputComposerRemoveResourceOptions) => {
    const shouldRemoveInputRefs = options?.removeInputRefs !== false;
    setSessionState((current) => {
      const nextResourceList = current.resourceList.filter((item) => !isSameInputComposerResource(item, resource));
      const hasRemovedResource = nextResourceList.length !== current.resourceList.length;
      const resourceIndex = resource.index ? `${resource.index}` : '';
      const hasReferencedResource = !!resourceIndex && referencedResourceIndexSetRef.current.has(resourceIndex);

      if (!shouldRemoveInputRefs) {
        return {
          resourceList: nextResourceList,
        };
      }

      const currentEditorContent = buildEditorContentFromInputText(
        current.inputText,
        current.resourceList,
        current.agentInfoList,
      );
      const nextEditorContent = removeResourceRefsFromEditorContent(
        currentEditorContent,
        resource,
      );
      const hasRemovedInputRefs = nextEditorContent.length !== currentEditorContent.length;
      shouldSyncInputRef.current = hasRemovedInputRefs;
      // 删除被 @ 引用过的资源时必须丢弃旧 undo 栈，避免撤销恢复已失效的 @资源引用。
      shouldResetInputHistoryRef.current = hasRemovedResource && (hasRemovedInputRefs || hasReferencedResource);
      if (hasRemovedResource && resourceIndex) {
        referencedResourceIndexSetRef.current.delete(resourceIndex);
      }
      if (hasRemovedInputRefs) {
        syncInputSetValueOptionsRef.current = { focus: true, selectEnd: true };
      }

      if (!hasRemovedInputRefs) {
        return {
          resourceList: nextResourceList,
        };
      }

      return {
        resourceList: nextResourceList,
        inputText: serializeContentToInlineText(nextEditorContent),
        agentInfoList: getAgentInfoListFromEditorContent(nextEditorContent),
      };
    });
  }, [setSessionState]);

  const clearResources = useCallback(() => {
    setSessionState({ resourceList: [] });
  }, [setSessionState]);

  const showUploadLimitToast = useCallback((type: 'image' | 'video' | 'file') => {
    if (type === 'image') {
      if (images.length >= maxImageNum) {
        message.info({ content: { key: 'RoboNeo_Web_upload_image_maximum', params: { max_num: maxImageNum } } });
        return;
      }
      if (resourceTotal >= maxResourceNum) {
        message.info({ content: { key: 'roboneo_chat_resources_num_limit_tips', params: { img_num: maxResourceNum - maxVideoNum, video_num: maxVideoNum } } });
      }
      return;
    }
    if (type === 'video') {
      if (videos.length >= maxVideoNum) {
        message.info({ content: { key: 'RoboNeo_Web_upload_video_maximum', params: { max_num: maxVideoNum } } });
        return;
      }
      if (resourceTotal >= maxResourceNum) {
        message.info({ content: { key: 'roboneo_chat_resources_num_limit_tips', params: { img_num: maxResourceNum - maxVideoNum, video_num: maxVideoNum } } });
      }
      return;
    }
    if (documents.length >= maxDocumentNum) {
      message.info({ content: { key: 'roboneo_file_limit' } });
      return;
    }
    if (resourceTotal >= maxResourceNum) {
      message.info({ content: { key: 'roboneo_chat_resources_num_limit_tips', params: { img_num: maxResourceNum - maxVideoNum, video_num: maxVideoNum } } });
    }
  }, [documents.length, images.length, maxDocumentNum, maxImageNum, maxResourceNum, maxVideoNum, resourceTotal, videos.length]);

  const uploadImages = useCallback(() => {
    const leftNums = Math.min(
      images.length >= maxImageNum ? 0 : maxImageNum - images.length,
      remainingResourceNum,
    );
    try {
      const input = handleClickUploadFile({
        location: trackLocation,
        type: FileTypeEnum.Image,
        importCount: leftNums,
        isCheckSafe: false,
        from: 'input',
        callback: (files) => {
          if (isCancelUpload.current === InputComposerCancelStatus.Cancelling) {
            setSessionState({ uploading: false, uploadingCount: 0 });
            isCancelUpload.current = InputComposerCancelStatus.Default;
            return;
          }
          setSessionState({ uploading: false, uploadingCount: 0 });
          isCancelUpload.current = InputComposerCancelStatus.Default;
          addResources(mapUploadedFilesToResources(files, 'image'));
        },
        changeBeforeUpload: (files) => {
          isCancelUpload.current = InputComposerCancelStatus.Cancel;
          setSessionState({
            uploading: true,
            uploadingCount: Math.min(files.length, leftNums),
          });
        },
      });
      input.click();
    } catch (error) {
      isCancelUpload.current = InputComposerCancelStatus.Default;
    }
  }, [addResources, images.length, maxImageNum, remainingResourceNum, setSessionState, trackLocation]);

  const uploadVideos = useCallback(() => {
    const leftNums = Math.min(
      videos.length >= maxVideoNum ? 0 : maxVideoNum - videos.length,
      remainingResourceNum,
    );
    try {
      const input = handleClickUploadFile({
        location: trackLocation,
        from: 'input',
        type: FileTypeEnum.Video,
        importCount: leftNums,
        isCheckSafe: false,
        callback: (files) => {
          if (isCancelUpload.current === InputComposerCancelStatus.Cancelling) {
            setSessionState({ uploading: false, uploadingCount: 0 });
            isCancelUpload.current = InputComposerCancelStatus.Default;
            return;
          }
          setSessionState({ uploading: false, uploadingCount: 0 });
          isCancelUpload.current = InputComposerCancelStatus.Default;
          addResources(mapUploadedFilesToResources(files, 'video'));
        },
        changeBeforeUpload: (files) => {
          isCancelUpload.current = InputComposerCancelStatus.Cancel;
          setSessionState({
            uploading: true,
            uploadingCount: Math.min(files.length, leftNums),
          });
        },
      });
      input.click();
    } catch (error) {
      isCancelUpload.current = InputComposerCancelStatus.Default;
    }
  }, [addResources, maxVideoNum, remainingResourceNum, setSessionState, trackLocation, videos.length]);

  const uploadDocument = useCallback(() => {
    const leftNums = Math.min(
      documents.length >= maxDocumentNum ? 0 : maxDocumentNum - documents.length,
      remainingResourceNum,
    );
    try {
      const input = handleClickUploadFile({
        location: trackLocation,
        from: 'input',
        type: FileTypeEnum.Document,
        importCount: leftNums,
        isCheckSafe: false,
        callback: (files) => {
          setSessionState({ uploading: false, uploadingCount: 0 });
          if (isCancelUpload.current === InputComposerCancelStatus.Cancelling || !files.length) return;
          addResources(mapUploadedFilesToResources(files, 'file'));
        },
        changeBeforeUpload: (files) => {
          isCancelUpload.current = InputComposerCancelStatus.Default;
          setSessionState({
            uploading: true,
            uploadingCount: Math.min(files.length, leftNums),
          });
        },
      });
      input.click();
    } catch (error) {
      setSessionState({ uploading: false, uploadingCount: 0 });
    }
  }, [addResources, documents.length, maxDocumentNum, remainingResourceNum, setSessionState, trackLocation]);

  const validateAssetMediaLimit = useCallback((list: InputPanelAssetModalListItem[]) => {
    let incomingImageCount = 0;
    let incomingVideoCount = 0;
    let incomingDocumentCount = 0;

    list.forEach((item) => {
      if (item.materialType === 'image') { incomingImageCount += 1; return; }
      if (item.materialType === 'video') { incomingVideoCount += 1; return; }
      if (isDocumentAssetType(item.materialType)) {
        incomingDocumentCount += 1;
      }
    });

    if (images.length + incomingImageCount > maxImageNum) {
      message.info({ content: { key: 'RoboNeo_Web_upload_image_maximum', params: { max_num: maxImageNum } } });
      return false;
    }
    if (videos.length + incomingVideoCount > maxVideoNum) {
      message.info({ content: { key: 'RoboNeo_Web_upload_video_maximum', params: { max_num: maxVideoNum } } });
      return false;
    }
    if (documents.length + incomingDocumentCount > maxDocumentNum) {
      message.info({ content: { key: 'roboneo_file_limit' } });
      return false;
    }
    if (resourceTotal + incomingImageCount + incomingVideoCount + incomingDocumentCount > maxResourceNum) {
      message.info({ content: { key: 'roboneo_chat_resources_num_limit_tips', params: { img_num: maxResourceNum - maxVideoNum, video_num: maxVideoNum } } });
      return false;
    }
    return true;
  }, [documents.length, images.length, maxDocumentNum, maxImageNum, maxResourceNum, maxVideoNum, resourceTotal, videos.length]);

  /** 添加资产库资源并返回真实添加结果，供调用方避免失败后继续弹成功提示。 */
  const addAssetResources = useCallback((items: InputPanelAssetModalListItem[]) => {
    if (!validateAssetMediaLimit(items)) return false;
    addResources(mapAssetItemsToResources(items));
    setSessionState({ assetLibraryVisible: false });
    return true;
  }, [addResources, setSessionState, validateAssetMediaLimit]);

  /** 处理拖拽/粘贴文件上传，沿用 HomeInputChat 的限制提示并写入新版资源预览区。 */
  const uploadDroppedFiles = useCallback((files: File[], eventType?: string) => {
    if (session.uploading || !files.length) return;

    let incomingImageCount = 0;
    let incomingVideoCount = 0;
    let incomingDocumentCount = 0;
    const mediaType = new Set<'image' | 'video' | 'file'>();

    files.forEach((file) => {
      const fileType = formatFileType(file);
      if (fileType === FileTypeEnum.Image) {
        incomingImageCount += 1;
        mediaType.add('image');
        return;
      }
      if (fileType === FileTypeEnum.Video) {
        incomingVideoCount += 1;
        mediaType.add('video');
        return;
      }
      incomingDocumentCount += 1;
      mediaType.add('file');
    });

    const triggerType: Record<string, string> = {
      drop: 'drag_drop',
      paste: 'paste',
    };

    mtstatTrack('upload_control_click', {
      location: trackLocation,
      media_type: Array.from(mediaType).join(','),
      trigger_type: triggerType[eventType || ''] || '',
      thinking_mode: session.agentThinkingModeType,
    });

    if (images.length + incomingImageCount > maxImageNum) {
      message.info({ content: { key: 'RoboNeo_Web_upload_image_maximum', params: { max_num: maxImageNum } } });
      return;
    }
    if (videos.length + incomingVideoCount > maxVideoNum) {
      message.info({ content: { key: 'RoboNeo_Web_upload_video_maximum', params: { max_num: maxVideoNum } } });
      return;
    }
    if (documents.length + incomingDocumentCount > maxDocumentNum) {
      message.info({ content: { key: 'roboneo_file_limit' } });
      return;
    }
    if (resourceTotal + incomingImageCount + incomingVideoCount + incomingDocumentCount > maxResourceNum) {
      message.info({ content: { key: 'roboneo_chat_resources_num_limit_tips', params: { img_num: maxResourceNum - maxVideoNum, video_num: maxVideoNum } } });
      return;
    }

    const imageImportCount = Math.min(Math.max(0, maxImageNum - images.length), remainingResourceNum);
    const videoImportCount = Math.min(Math.max(0, maxVideoNum - videos.length), remainingResourceNum);
    const documentImportCount = Math.min(Math.max(0, maxDocumentNum - documents.length), remainingResourceNum);

    try {
      dealUploadFile(
        {
          location: trackLocation,
          from: 'input',
          files,
          controlImagesOptions: {
            importCount: imageImportCount,
            maxTotal: maxImageNum,
          },
          controlVideosOptions: {
            importCount: videoImportCount,
          },
          controlFilesOptions: {
            importCount: documentImportCount,
          },
          controlAudiosOptions: {
            importCount: documentImportCount,
          },
          isCheckSafe: false,
        },
        {
          callback: (fileList) => {
            if (isCancelUpload.current === InputComposerCancelStatus.Cancelling) {
              setSessionState({ uploading: false, uploadingCount: 0 });
              isCancelUpload.current = InputComposerCancelStatus.Default;
              return;
            }
            setSessionState({ uploading: false, uploadingCount: 0 });
            isCancelUpload.current = InputComposerCancelStatus.Default;
            if (!fileList.length) return;
            addResources(mapUploadCallbackFilesToResources(fileList));
          },
          changeBeforeUpload: (uploadFiles) => {
            isCancelUpload.current = InputComposerCancelStatus.Cancel;
            setSessionState({
              uploading: true,
              uploadingCount: uploadFiles.length,
            });
          },
        },
      );
    } catch (error) {
      isCancelUpload.current = InputComposerCancelStatus.Default;
      setSessionState({ uploading: false, uploadingCount: 0 });
    }
  }, [
    addResources,
    documents.length,
    images.length,
    maxDocumentNum,
    maxImageNum,
    maxResourceNum,
    maxVideoNum,
    remainingResourceNum,
    resourceTotal,
    session.agentThinkingModeType,
    session.uploading,
    setSessionState,
    trackLocation,
    videos.length,
  ]);

  const openAssetLibrary = useCallback(async () => {
    const allow = await beforeOpenAssetLibrary?.();
    if (allow === false) return;
    onAssetLibraryOpen?.();
    setSessionState({ assetLibraryVisible: true });
  }, [beforeOpenAssetLibrary, onAssetLibraryOpen, setSessionState]);

  const closeAssetLibrary = useCallback(() => {
    setSessionState({ assetLibraryVisible: false });
  }, [setSessionState]);

  const uploadButtonList = useMemo(() => [
    {
      key: 'image',
      icon: 'pictureBold',
      text: languageStore.smartcatTranslationText((v: any) => v.RoboNeo_Web_chatbox_add_image),
      onClick: () => uploadImages(),
      disabled: imageUploadDisabled,
    },
    {
      key: 'video',
      icon: 'videoV4Bold',
      text: languageStore.smartcatTranslationText((v: any) => v.RoboNeo_Web_chatbox_add_video),
      onClick: () => uploadVideos(),
      disabled: videoUploadDisabled,
    },
    {
      key: 'file',
      icon: 'paperClipBold',
      text: languageStore.smartcatTranslationText((v: any) => v.roboneo_add_file),
      onClick: () => uploadDocument(),
      disabled: documentUploadDisabled,
    },
    {
      key: 'asset',
      icon: 'clockBold',
      text: languageStore.smartcatTranslationText((v: any) => v.RoboNeo_Web_history_text),
      onClick: () => { openAssetLibrary(); },
      disabled: session.uploading || (documentUploadDisabled && videoUploadDisabled && imageUploadDisabled),
    },
  ], [documentUploadDisabled, imageUploadDisabled, languageStore, openAssetLibrary, session.uploading, uploadDocument, uploadImages, uploadVideos, videoUploadDisabled]);

  const initInputButtonConfig = useCallback(async () => {
    try {
      const res = await getSoraStatus();
      const defaultValues = {
        showDocumentUpload: false,
        showModelSelect: false,
        showSelectMode: false,
        showAgentThinkingMode: false,
      };

      if (res?.input) {
        const patch: Partial<InputComposerSession> = {};
        res.input.forEach((item) => {
          if (item.type === 6) {
            patch.showDocumentUpload = item.enable;
            defaultValues.showDocumentUpload = item.enable;
          }
          if (item.type === 5) {
            patch.showModelSelect = item.enable;
            defaultValues.showModelSelect = item.enable;
          }
          if (item.type === 7 && config.enableSelectMode) {
            patch.showSelectMode = item.enable;
            defaultValues.showSelectMode = item.enable;
          }
          if (item.type === 8 && config.enableThinkingMode) {
            patch.showAgentThinkingMode = item.enable;
            defaultValues.showAgentThinkingMode = item.enable;
          }
        });
        setSessionState({
          ...patch,
          defaultInputToolsVisibleValues: defaultValues,
        });
      }

      if (config.forceShowModelSelect) {
        setSessionState((current) => ({
          showModelSelect: true,
          defaultInputToolsVisibleValues: {
            ...current.defaultInputToolsVisibleValues,
            showModelSelect: true,
          },
        }));
      }
    } catch (error) {
      console.log(error);
    }
  }, [config.enableSelectMode, config.enableThinkingMode, config.forceShowModelSelect, setSessionState]);

  const initModelConfig = useCallback(async () => {
    const res = await roomItemModelList();
    const modelMap = modelMapFormat(res);
    const menuDetail = modelMapFormatMenuDetail(res);
    const modelList = res.parameter.thinking_mode_info?.mode_items || [];
    const thinkingModeList = modelList.map((item) => {
      const icon = item.mode_value === 'fast' ? 'zapBold' : 'lightBulbBold';
      return {
        modelName: item.mode_title,
        modelValue: item.mode_value,
        modelDesc: item.mode_desc,
        icon: item.icon ? item.icon : icon,
      };
    });

    setSessionState((current) => ({
      modelMap,
      modelPatternInfoList: menuDetail,
      activeMode: res.parameter.menu_detail.selected_model_pattern,
      agentThinkingModeList: thinkingModeList,
      agentThinkingModeType: current.agentThinkingModeType
        || res.parameter.thinking_mode_info?.default_mode_value
        || '',
    }));
  }, [setSessionState]);

  const initSkillData = useCallback(async () => {
    if (config.enableSkillSelect === false) return;
    try {
      const skillData = await getHomeInputPrompt();
      setSessionState({
        skillCategories: skillData.categories,
        skillList: skillData.list || [],
        showSkillSelect: skillData.categories.length > 0,
      });
    } catch (error) {
      console.log(error);
    }
  }, [config.enableSkillSelect, setSessionState]);

  const actions = useMemo<InputComposerActions>(() => ({
    inputRef,
    setInputText,
    setEditorContent,
    setPopupDataContent,
    resetEditorContent: () => {
      setInputText('', { syncInput: true });
    },
    addResources,
    setResources,
    removeResource,
    clearResources,
    setUploadPopoverOpen: (open) => {
      setSessionState({ uploadPopoverOpen: open });
    },
    openAssetLibrary,
    closeAssetLibrary,
    uploadImages,
    uploadVideos,
    uploadDocument,
    uploadDroppedFiles,
    showUploadLimitToast,
    addAssetResources,
    setUsedModel: (usedModel) => {
      setSessionState({ usedModel });
    },
    setSelectedSkill: (skill) => {
      setSessionState({
        selectedSkill: normalizeSelectedSkill(skill),
      });
    },
    // 从已选 Skill 列表中移除指定项，供预览区关闭按钮使用。
    removeSelectedSkill: (skill) => {
      setSessionState((current) => ({
        selectedSkill: current.selectedSkill.filter(
          (item) => !(item.id === skill.id && item.category_id === skill.category_id),
        ),
      }));
    },
    initInputButtonConfig,
    initModelConfig,
    initSkillData,
  }), [
    addAssetResources,
    addResources,
    clearResources,
    closeAssetLibrary,
    initInputButtonConfig,
    initModelConfig,
    initSkillData,
    openAssetLibrary,
    removeResource,
    setEditorContent,
    setInputText,
    setPopupDataContent,
    setResources,
    setSessionState,
    showUploadLimitToast,
    uploadDroppedFiles,
    uploadDocument,
    uploadImages,
    uploadVideos,
  ]);

  return {
    ...session,
    editorContent,
    serializedContent,
    images,
    videos,
    documents,
    atImageOptions,
    usedModelName,
    selectedSkillInfo,
    selectedSkillInfoList,
    isSubmitDisabled,
    imageUploadDisabled,
    videoUploadDisabled,
    documentUploadDisabled,
    uploadButtonList,
    actions,
  };
}
